import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Container, Row, Col, Button } from 'reactstrap';
import { withTranslation } from 'react-i18next';

/**
 * Компонент для перехвата и отображения критических ошибок приложения.
 * Используется как граница ошибок (Error Boundary) для React компонентов.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Обновить состояние, чтобы следующий рендер показал запасной UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Логирование ошибки
    console.error('ErrorBoundary caught error:', error, errorInfo);

    // Сохраняем детали ошибки в состоянии
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Отправляем ошибку в систему мониторинга (если настроена)
    if (typeof window.onCriticalError === 'function') {
      window.onCriticalError({
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      const { error, errorInfo } = this.state;

      return (
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <div className="text-center mb-4">
                <i className="mdi mdi-alert-circle-outline"
                   style={{ fontSize: '4.5rem', color: '#f46a6a' }}></i>
                <h3 className="mt-3 mb-2">{t('error.occurred')}</h3>
                <p className="text-muted">
                  {t('error.somethingWentWrong')}
                </p>
              </div>

              <Alert color="danger" className="mb-4">
                <h5 className="alert-heading">
                  <i className="mdi mdi-information-outline me-2"></i>
                  {t('error.technicalInfo')}
                </h5>
                <p className="mb-2">
                  <strong>{t('error.errorMessage')}:</strong> {error?.message || t('error.unknownError')}
                </p>
                {process.env.NODE_ENV === 'development' && (
                  <details className="mt-3">
                    <summary className="cursor-pointer">
                      {t('error.detailsDevOnly')}
                    </summary>
                    <pre className="mt-2 small" style={{
                      background: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}>
                      {error?.stack}
                      {errorInfo?.componentStack}
                    </pre>
                  </details>
                )}
              </Alert>

              <div className="text-center">
                <Button
                  color="primary"
                  className="me-2 mb-2"
                  onClick={this.handleReload}
                >
                  <i className="mdi mdi-reload me-2"></i>
                  {t('error.reloadPage')}
                </Button>
                <Button
                  color="secondary"
                  className="mb-2"
                  onClick={this.handleGoHome}
                >
                  <i className="mdi mdi-home me-2"></i>
                  {t('error.goHome')}
                </Button>
              </div>

              <div className="text-center mt-4">
                <small className="text-muted">
                  {t('error.contactAdmin')}
                </small>
              </div>
            </Col>
          </Row>
        </Container>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  /** Дочерние компоненты */
  children: PropTypes.node.isRequired,
  /** Функция перевода (инжектируется withTranslation) */
  t: PropTypes.func.isRequired,
};

export default withTranslation('errors')(ErrorBoundary);
